import { GetServerSidePropsContext } from "next";

import { ENV, DOMAIN_WHEN_LOCALHOST, ENABLE_TOOLKIT } from "@/src/const";
import { CMSLogic } from "@/src/logic";
import { StrapiCollectionType } from "@/src/type";
import { NextButtonPosition } from "@/src/type/strapi/collection";
import { StringHelper } from "@/src/util";

function isToolkitEnabled(hostname: string) {
  return (
    ENABLE_TOOLKIT || hostname === "dev-toolkit.notflow.life" || hostname === "toolkit.notflow.life" || hostname === "stage-toolkit.notflow.life"
  );
}

function getPathRootPrefix(hostname: string) {
  if (!hostname) {
    return "c";
  }

  const flowSites = ["flow.life", "notflow.life", "www.flow.life", "notflow.life", "www.tracemidtown.com", "tracemidtown.com"];
  if (flowSites.includes(hostname)) {
    return undefined;
  }
  return "c";
}

function getDomainWhenLocalhost(): string {
  const domain = (DOMAIN_WHEN_LOCALHOST ?? "").length > 1 ? DOMAIN_WHEN_LOCALHOST : undefined;
  switch (ENV) {
    case "dev":
    case "local":
      return domain ?? "dev.notflow.life";
    case "prod":
      return domain ?? "flow.life";
    default:
      return "localhost";
  }
}

function processPage<T extends Record<string, unknown>>(target: unknown, context: T, processContext: boolean = true): unknown {
  const propertiesToRemove = ["createdAt", "updatedAt", "publishedAt", "formats", "previewUrl", "provider_metadata"];
  const contextKeys = Object.keys(context);
  const contextValues = Object.values(context);

  function processValue(value: unknown): unknown {
    if (typeof value === "string" && value.includes("${") && processContext) {
      return value.replace(/\$\{([^}]+)\}/g, (match, expr) => {
        try {
          const func = new Function(...contextKeys, `return ${expr.trim()}`);
          const result = func(...contextValues);
          return result != null ? String(result) : "";
        } catch (error) {
          console.warn(`String expression evaluation failed: ${match}`, error);
          return match;
        }
      });
    }

    if (Array.isArray(value)) {
      return value.map(processValue);
    }

    if (value && typeof value === "object") {
      const result: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(value)) {
        if (propertiesToRemove.includes(key)) {
          continue;
        }

        const value = processValue(val);
        if (value !== undefined && value !== null) {
          result[key] = value;
        }
      }
      return result;
    }

    return value;
  }

  return processValue(target);
}

function getAllParams(context: GetServerSidePropsContext) {
  const domain = StringHelper.checkIsStringOrIsNull(context.req.headers?.["x-forwarded-host"] ?? context.req.headers?.host, "flow.life");
  const hostname = domain.includes("localhost") ? getDomainWhenLocalhost() : domain.split(":")[0];
  const isToolkit = isToolkitEnabled(hostname);
  const toolkitDomain = StringHelper.checkIsStringOrIsNull(context?.query?.toolkitDomain, "").toLowerCase();
  const toolkitHostname = isToolkit ? (toolkitDomain ?? getDomainWhenLocalhost()) : hostname;
  const byPassCache: boolean = StringHelper.checkIsStringOrIsNull(context?.query?.cache, "false").toLowerCase() === "bypass";
  const isPreview: boolean = StringHelper.checkIsStringOrIsNull(context?.query?.preview, "false").toLowerCase() === "true";
  const returnHeaders: boolean = StringHelper.checkIsStringOrIsNull(context?.query?.headers, "false").toLowerCase() === "true";
  const trackingOff: boolean = StringHelper.checkIsStringOrIsNull(context?.query?.tracking, "false").toLowerCase() === "disable";
  const locale: string = isPreview ? "en-PW" : StringHelper.checkIsStringOrIsNull(context?.params?.locale, "en").toLowerCase();

  return {
    domain,
    hostname: isToolkit ? toolkitHostname : hostname,
    byPassCache,
    isPreview,
    returnHeaders,
    trackingOff,
    locale,
    isToolkit,
  };
}

export function getHeaderInfoForCurrentSection(page?: StrapiCollectionType.DynamicPage, activeIndex?: number) {
  if (!page || typeof activeIndex !== "number") {
    return null;
  }
  const currentSection = page?.dynamic_page_sections?.data?.[activeIndex ?? 0];
  if (!currentSection) {
    return null;
  }

  const header = currentSection?.attributes?.header;
  const backgroundColor = CMSLogic.colorSelector(currentSection?.attributes?.background?.color) ?? "transparent";
  return {
    header,
    backgroundColor,
  };
}

export function getCurrentSection(searchParams: any, page: StrapiCollectionType.DynamicPage): number {
  try {
    const sectionTag = StringHelper.checkIsStringOrIsNull(searchParams?.["section"], "false");
    if (sectionTag !== "false") {
      const section = page?.dynamic_page_sections?.data?.find((section) => section.attributes?.slug === sectionTag);
      if (section) {
        const index = page?.dynamic_page_sections?.data?.indexOf(section);
        if (index >= 0) {
          return index;
        }
      }
    }
    return parseInt(StringHelper.checkIsStringOrIsNull(searchParams?.["active"], "0"), 10);
  } catch (err) {
    return 0;
  }
}

export function getDomainData(hostname: string, page: StrapiCollectionType.DynamicPage): StrapiCollectionType.Domain | undefined {
  if (!hostname || !page) {
    return;
  }
  return (page.domains?.data ?? []).find((d) => d.attributes?.hostname === hostname)?.attributes;
}

type NextIconType = {
  nextButtonText?: string | null;
  nextIconColor: "light" | "dark";
  nextButtonPosition?: NextButtonPosition | null;
};

export function getCurrentNextIcon(page: StrapiCollectionType.DynamicPage, currentSection: number): NextIconType {
  const nextSection = page?.dynamic_page_sections?.data?.[currentSection ?? 0];
  return {
    nextButtonText: nextSection?.attributes?.nextButtonText ?? null,
    nextIconColor: nextSection?.attributes?.nextIconColor ?? "light",
    nextButtonPosition: nextSection?.attributes?.nextButtonPosition ?? page?.nextButtonPosition ?? null,
  };
}

export default {
  getAllParams,
  getDomainWhenLocalhost,
  isToolkitEnabled,
  getPathRootPrefix,
  processPage,
  getHeaderInfoForCurrentSection,
  getCurrentSection,
  getDomainData,
  getCurrentNextIcon,
};
